import React, { useState, useRef, useEffect, useCallback } from "react";
import api from "../../../api/api";
import "./PulseChat.css";
import { getPracticeId } from "../../../config/practice";

const practiceId = getPracticeId();

/* ── Starter prompts shown on first open ── */
const STARTERS = [
  { icon: "👋", text: "Summarize this patient" },
  { icon: "🔬", text: "Any abnormal labs?" },
  { icon: "📋", text: "What happened last visit?" },
  { icon: "💊", text: "Current medications?" },
];

/* ── Pulse avatar SVG ── */
const PulseAvatar = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="20" fill="url(#pulseGrad)" />
    {/* Heartbeat / pulse line */}
    <path
      d="M8 21h5l2-5 3 10 3-8 2 3h5"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <defs>
      <linearGradient id="pulseGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor="#E8653A" />
        <stop offset="1" stopColor="#F4A68A" />
      </linearGradient>
    </defs>
  </svg>
);

/* ── Typing dots animation ── */
const TypingDots = () => (
  <div className="pulse-typing">
    <span /><span /><span />
  </div>
);

/* ── Single chat message bubble ── */
const MessageBubble = ({ msg, onRefClick }) => {
  const isUser = msg.role === "user";

  // Parse message content, splitting on [REF:{...}] tags to render them inline
  const renderContent = () => {
    if (!msg.content) return null;
    const refPattern = /\[REF:(\{[^}]+\})\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = refPattern.exec(msg.content)) !== null) {
      // Add text before this ref
      if (match.index > lastIndex) {
        const textBefore = msg.content.slice(lastIndex, match.index);
        parts.push(
          <span key={`t-${lastIndex}`} dangerouslySetInnerHTML={{ __html: formatMessage(textBefore) }} />
        );
      }
      // Add the inline ref chip
      try {
        const ref = JSON.parse(match[1]);
        parts.push(
          <button
            key={`r-${match.index}`}
            className={`pulse-ref-chip-inline pulse-ref-${ref.type}`}
            onClick={() => onRefClick(ref)}
            title={ref.date ? `${ref.label} (${ref.date})` : ref.label}
          >
            <span className="pulse-ref-icon">{refIcon(ref.type)}</span>
            <span className="pulse-ref-label">{ref.label}</span>
          </button>
        );
      } catch {
        // If JSON parse fails, just skip the tag
      }
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last ref
    if (lastIndex < msg.content.length) {
      const remaining = msg.content.slice(lastIndex);
      parts.push(
        <span key={`t-${lastIndex}`} dangerouslySetInnerHTML={{ __html: formatMessage(remaining) }} />
      );
    }

    // If no refs found, just format the whole message
    if (parts.length === 0) {
      return <span dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />;
    }

    return parts;
  };

  return (
    <div className={`pulse-msg-row ${isUser ? "pulse-msg-user" : "pulse-msg-bot"}`}>
      {!isUser && (
        <div className="pulse-msg-avatar">
          <PulseAvatar size={26} />
        </div>
      )}
      <div className={`pulse-msg-bubble ${isUser ? "pulse-bubble-user" : "pulse-bubble-bot"}`}>
        <div className="pulse-msg-text">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

/* ── Helpers ── */
function refIcon(type) {
  switch (type) {
    case "lab": return "🔬";
    case "encounter": return "📋";
    case "imaging": return "🩻";
    case "medication": return "💊";
    case "condition": return "🩺";
    default: return "📎";
  }
}

function formatMessage(text) {
  if (!text) return "";
  // Bold **text**
  let html = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  // Bullet points
  html = html.replace(/^[-•]\s+(.+)$/gm, "<li>$1</li>");
  if (html.includes("<li>")) {
    html = html.replace(/(<li>.*?<\/li>)+/gs, (match) => `<ul>${match}</ul>`);
  }
  // Line breaks
  html = html.replace(/\n/g, "<br/>");
  return html;
}


/* ═══════════════════════════════════════════════════════
   PulseChat — main chat panel component
   ═══════════════════════════════════════════════════════ */
const PulseChat = ({ patientId, patientName, onClose, onRefClick }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: "user", content: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      // Build history (exclude current message)
      const history = newMessages.slice(0, -1).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await api.post(`/v1/kg/${practiceId}/pulse/${patientId}`, {
        message: text.trim(),
        history,
      });

      const botMsg = {
        role: "assistant",
        content: res.reply || res.data?.reply || "I couldn't generate a response.",
        references: res.references || res.data?.references || [],
      };

      setMessages((prev) => [...prev, botMsg]);
      if (!greeted) setGreeted(true);
    } catch (err) {
      console.error("Pulse error:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I ran into an issue. Please try again.",
          references: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, patientId, greeted]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="pulse-panel">
      {/* Header */}
      <div className="pulse-header">
        <div className="pulse-header-left">
          <PulseAvatar size={28} />
          <div>
            <div className="pulse-header-title">Pulse</div>
            <div className="pulse-header-sub">
              {patientName ? `Reviewing ${patientName}'s chart` : "Chart Assistant"}
            </div>
          </div>
        </div>
        <button className="pulse-close-btn" onClick={onClose} title="Close Pulse">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" /><path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Messages area */}
      <div className="pulse-messages" ref={scrollRef}>
        {messages.length === 0 && !loading && (
          <div className="pulse-welcome">
            <PulseAvatar size={48} />
            <div className="pulse-welcome-title">Hi, I'm Pulse</div>
            <div className="pulse-welcome-sub">
              Ask me anything about this patient's chart.
            </div>
            <div className="pulse-starters">
              {STARTERS.map((s, i) => (
                <button
                  key={i}
                  className="pulse-starter-btn"
                  onClick={() => sendMessage(s.text)}
                >
                  <span>{s.icon}</span> {s.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} onRefClick={onRefClick} />
        ))}

        {loading && (
          <div className="pulse-msg-row pulse-msg-bot">
            <div className="pulse-msg-avatar">
              <PulseAvatar size={26} />
            </div>
            <div className="pulse-msg-bubble pulse-bubble-bot">
              <TypingDots />
            </div>
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="pulse-input-area">
        <div className="pulse-input-wrap">
          <textarea
            ref={inputRef}
            className="pulse-input"
            placeholder="Ask about this patient..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={loading}
          />
          <button
            className="pulse-send-btn"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m22 2-7 20-4-9-9-4z" /><path d="m22 2-11 11" />
            </svg>
          </button>
        </div>
        <div className="pulse-input-hint">
          Press Enter to send
        </div>
      </div>
    </div>
  );
};

export { PulseAvatar };
export default PulseChat;
